import http from './http';

export interface ExtractGoalsResponse {
    goals: string[];
}

export interface Goal {
    id: string;
    title: string;
    description?: string;
    reminderTimes: string[];
    participantId: string;
    createdBy?: string;
    noteId?: string;
    confidenceScore?: number | null;
    createdAt: string;
    updatedAt: string;
}

export interface GoalProgress {
    id: string;
    progressDate: string;
    status: 'not_started' | 'in_progress' | 'completed';
    createdAt: string;
}

export interface GoalWithProgress extends Goal {
    // Progress history
    progress: GoalProgress[];
}

export interface CreateGoalData {
    title: string;
    description?: string;
    reminderTimes?: string[];
    participantId: string;
    noteId?: string;
    confidenceScore?: number;
}

export interface UpdateGoalData {
    title?: string;
    description?: string;
    reminderTimes?: string[];
    confidenceScore?: number | null;
}

export interface GetGoalsFilters {
    participantId?: string;
}

export const goalsApi = {
    extractGoals: async (noteId: string): Promise<ExtractGoalsResponse> => {
        const response = await http.post(`/goals/extract/${noteId}`);
        return response.data.data;
    },

    createGoal: async (data: CreateGoalData): Promise<Goal> => {
        const response = await http.post('/goals', data);
        return response.data.data;
    },

    createBulkGoals: async (goals: CreateGoalData[]): Promise<Goal[]> => {
        const response = await http.post('/goals/bulk', { goals });
        return response.data.data;
    },

    getGoals: async (filters?: GetGoalsFilters): Promise<Goal[]> => {
        const params = new URLSearchParams();
        if (filters?.participantId) params.append('participantId', filters.participantId);

        const queryString = params.toString();
        const url = queryString ? `/goals?${queryString}` : '/goals';
        const response = await http.get(url);
        return response.data.data;
    },

    getGoalProgress: async (goalId: string, startDate: string, endDate: string): Promise<GoalProgress[]> => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        const response = await http.get(`/goals/${goalId}/progress?${params.toString()}`);
        return response.data.data;
    },

    getGoalById: async (id: string): Promise<GoalWithProgress> => {
        const response = await http.get(`/goals/${id}`);
        return response.data.data;
    },

    updateGoal: async (id: string, data: UpdateGoalData): Promise<Goal> => {
        const response = await http.patch(`/goals/${id}`, data);
        return response.data.data;
    },

    deleteGoal: async (id: string): Promise<void> => {
        await http.delete(`/goals/${id}`);
    },

    /** Participant only: set confidence 0–100 */
    updateMyGoalConfidence: async (
        goalId: string,
        confidenceScore: number,
    ): Promise<Goal> => {
        const response = await http.patch(
            `/participant/goals/${goalId}/confidence`,
            { confidenceScore },
        );
        return response.data.data;
    },
};
